import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { LeftPanel } from '../components/LeftPanel';
import { FleetMap } from '../components/FleetMap';
import { DriverDetail } from '../components/DriverDetail';
import { DemoControls } from '../components/DemoControls';
import { DesignNotes } from '../components/DesignNotes';
import { SuccessToasts, SuccessToastData } from '../components/ToastNotifications';
import { CallCard } from '../components/CallCard';
import { mockDrivers, mockAlerts, mockDeliveries, mockTriageItems } from '../data/mockData';
import { LogEntry } from '../components/LogsPanel';
import { AlertTriangle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// IDs to suppress in calm phase
const CRITICAL_WARNING_ALERT_IDS = ['a-hos-marcus', 'a-off-sarah', 'a-traf-james', 'a-maint-james'];
const DRIVERS_TO_NORMALIZE = ['d-marcus', 'd-sarah', 'd-james'];
export function Flow2() {
  // Scripted Flow State
  const [phase, setPhase] = useState<'calm' | 'traffic'>('calm');
  const [showToast, setShowToast] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('traffic');
      setShowToast(true);
      // Play warning alert sound (softer than critical)
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(660, ctx.currentTime);
          osc.frequency.setValueAtTime(520, ctx.currentTime + 0.12);
          gain.gain.setValueAtTime(0, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.02);
          gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.15);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.22);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.22);
        }
      } catch (e) {
        // Audio not available
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);
  // Auto-dismiss toast after 8 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);
  // Override mock data based on phase
  const currentDrivers = useMemo(() => {
    return mockDrivers.map(d => {
      if (phase === 'calm' && DRIVERS_TO_NORMALIZE.includes(d.id)) {
        return {
          ...d,
          status: 'normal' as const
        };
      }
      if (phase === 'traffic') {
        // Marcus and Sarah stay normal, James becomes warning
        if (d.id === 'd-marcus' || d.id === 'd-sarah') {
          return {
            ...d,
            status: 'normal' as const
          };
        }
        if (d.id === 'd-james') {
          return {
            ...d,
            status: 'warning' as const,
            route: d.route.map(s => s.id === 's2' ? {
              ...s,
              eta: '14:45'
            } : s)
          };
        }
      }
      return d;
    });
  }, [phase]);
  const currentAlerts = useMemo(() => {
    if (phase === 'calm') {
      // Hide all critical/warning alerts
      return mockAlerts.filter(a => !CRITICAL_WARNING_ALERT_IDS.includes(a.id));
    }
    // In traffic phase, show only the traffic alert (no HOS, offline, or maintenance)
    return mockAlerts.filter(a => a.id !== 'a-hos-marcus' && a.id !== 'a-off-sarah' && a.id !== 'a-maint-james');
  }, [phase]);
  // Standard Dashboard State (identical to Flow1/Dashboard)
  const [demoState, setDemoState] = useState<'normal' | 'hos' | 'offline' | 'bridge'>('normal');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [highlightedDriverIds, setHighlightedDriverIds] = useState<string[] | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [alertHoveredDriverIds, setAlertHoveredDriverIds] = useState<string[] | null>(null);
  const [callingDriverId, setCallingDriverId] = useState<string | null>(null);
  const [resolvedAlertIds, setResolvedAlertIds] = useState<string[]>([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState<string[]>([]);
  const [isRerouting, setIsRerouting] = useState(false);
  const [hoveredRouteId, setHoveredRouteId] = useState<number | null>(null);
  const [successToasts, setSuccessToasts] = useState<SuccessToastData[]>([]);
  const [routeOverrides, setRouteOverrides] = useState<Record<string, import('../data/mockData').RouteStop[]>>({});
  const [statusOverrides, setStatusOverrides] = useState<Record<string, import('../data/mockData').Severity>>({});
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const addLogEntry = useCallback((entry: Omit<LogEntry, 'id' | 'timestamp'>) => {
    setLogEntries(prev => [...prev, {
      ...entry,
      id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: Date.now()
    }]);
  }, []);
  const handlePreviewRoute = (driverId: string, stops: import('../data/mockData').RouteStop[]) => {
    setRouteOverrides(prev => ({
      ...prev,
      [driverId]: stops
    }));
    const driver = currentDrivers.find(d => d.id === driverId);
    if (driver) {
      const originalPending = driver.route.filter(s => s.status !== 'completed').length;
      const newPending = stops.filter(s => s.status !== 'completed').length;
      if (newPending < originalPending) {
        setStatusOverrides(prev => ({
          ...prev,
          [driverId]: 'normal'
        }));
      } else {
        setStatusOverrides(prev => {
          const next = {
            ...prev
          };
          delete next[driverId];
          return next;
        });
      }
    }
  };
  const handleCancelRoutePreview = (driverId: string) => {
    setRouteOverrides(prev => {
      const next = {
        ...prev
      };
      delete next[driverId];
      return next;
    });
    setStatusOverrides(prev => {
      const next = {
        ...prev
      };
      delete next[driverId];
      return next;
    });
  };
  const handleUpdateRoute = (driverId: string, stops: import('../data/mockData').RouteStop[]) => {
    const driver = currentDrivers.find(d => d.id === driverId);
    const originalPending = driver?.route.filter(s => s.status !== 'completed').length ?? 0;
    const newPending = stops.filter(s => s.status !== 'completed').length;
    setRouteOverrides(prev => ({
      ...prev,
      [driverId]: stops
    }));
    if (newPending < originalPending && driver) {
      const driverAlertIds = currentAlerts.filter(a => a.driverId === driverId || a.affectedDriverIds?.includes(driverId)).map(a => a.id);
      if (driverAlertIds.length > 0) {
        setResolvedAlertIds(prev => [...prev, ...driverAlertIds.filter(id => !prev.includes(id))]);
      }
      setStatusOverrides(prev => ({
        ...prev,
        [driverId]: 'normal'
      }));
    }
    if (driver) {
      const now = Date.now();
      const newToasts: SuccessToastData[] = [{
        id: `edit-stops-${now}`,
        message: `${driver.truckId} stops updated`,
        detail: 'Change has been logged and sent to driver.'
      }];
      const originalStopIds = new Set(driver.route.map(s => s.id));
      const newStopIds = new Set(stops.map(s => s.id));
      const removedStopIds = [...originalStopIds].filter(id => !newStopIds.has(id));
      const undeliveredCargo = driver.cargo.filter(c => removedStopIds.includes(c.stopId) && c.status !== 'delivered');
      if (undeliveredCargo.length > 0) {
        const itemCount = undeliveredCargo.length;
        const itemNames = undeliveredCargo.map(c => c.commodity).join(', ');
        newToasts.push({
          id: `undelivered-${now}`,
          message: `${itemCount} item${itemCount > 1 ? 's' : ''} moved to undelivered`,
          detail: itemNames,
          variant: 'warning'
        });
        addLogEntry({
          type: 'warning',
          message: `${itemCount} item${itemCount > 1 ? 's' : ''} moved to undelivered`,
          detail: itemNames,
          truckId: driver.truckId
        });
      }
      setSuccessToasts(prev => [...prev, ...newToasts]);
      addLogEntry({
        type: 'route-update',
        message: `Stops updated for ${driver.truckId}`,
        detail: `${stops.filter(s => s.status !== 'completed').length} pending stops. ${removedStopIds.length > 0 ? `${removedStopIds.length} stop${removedStopIds.length > 1 ? 's' : ''} removed.` : 'Stops reordered.'}`,
        truckId: driver.truckId
      });
    }
  };
  const handleApplyReroute = (driverId: string, suggestion: {
    etaImpactMin: number;
    label: string;
  }) => {
    const driver = currentDrivers.find(d => d.id === driverId);
    if (!driver) return;
    const updatedStops = driver.route.map(stop => {
      if (stop.status === 'completed') return stop;
      const etaClean = stop.eta.replace('~', '');
      const [h, m] = etaClean.split(':').map(Number);
      const totalMin = h * 60 + m + suggestion.etaImpactMin;
      const newH = Math.floor(totalMin / 60) % 24;
      const newM = totalMin % 60;
      const newEta = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
      let newStatus = stop.status;
      if (suggestion.etaImpactMin < 0 && stop.status === 'at-risk') {
        newStatus = 'pending';
      }
      return {
        ...stop,
        eta: newEta,
        status: newStatus
      };
    });
    setRouteOverrides(prev => ({
      ...prev,
      [driverId]: updatedStops
    }));
    if (suggestion.etaImpactMin < 0) {
      setStatusOverrides(prev => ({
        ...prev,
        [driverId]: 'normal'
      }));
    }
    // Resolve traffic alert for this driver after reroute
    const driverAlertIds = currentAlerts.filter(a => a.driverId === driverId || a.affectedDriverIds?.includes(driverId)).filter(a => a.type === 'traffic').map(a => a.id);
    if (driverAlertIds.length > 0) {
      setResolvedAlertIds(prev => [...prev, ...driverAlertIds.filter(id => !prev.includes(id))]);
    }
    setIsRerouting(false);
    setHoveredRouteId(null);
    setSuccessToasts(prev => [...prev, {
      id: `reroute-${Date.now()}`,
      message: `${driver.truckId} route updated to ${suggestion.label}`,
      detail: 'Change has been logged and sent to driver.'
    }]);
    addLogEntry({
      type: 'reroute',
      message: `Rerouted to ${suggestion.label}`,
      detail: `ETA impact: ${suggestion.etaImpactMin > 0 ? '+' : ''}${suggestion.etaImpactMin} min`,
      truckId: driver.truckId
    });
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'h' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        setShowHidden(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  useEffect(() => {
    if (demoState === 'hos') {
      setSelectedDriverId('d-marcus');
      setSelectedAlertId('a-hos-marcus');
    } else if (demoState === 'offline') {
      setSelectedDriverId('d-sarah');
      setSelectedAlertId('a-off-sarah');
    } else if (demoState === 'bridge') {
      setSelectedDriverId('d-david');
      setSelectedAlertId('a-brg-david');
    } else {
      setSelectedDriverId(null);
      setSelectedAlertId(null);
      setHighlightedDriverIds(null);
    }
  }, [demoState]);
  const handleSelectAlert = (alertId: string, driverId: string) => {
    setSelectedAlertId(alertId);
    const alert = currentAlerts.find(a => a.id === alertId);
    if (alert?.affectedDriverIds && alert.affectedDriverIds.length > 0) {
      setHighlightedDriverIds(alert.affectedDriverIds);
      setSelectedDriverId(null);
    } else {
      setHighlightedDriverIds(null);
      setSelectedDriverId(driverId);
    }
  };
  const [scrollToRouteTs, setScrollToRouteTs] = useState<number | null>(null);
  const [selectedDeliveryRef, setSelectedDeliveryRef] = useState<string | null>(null);
  const handleSelectDriver = (driverId: string) => {
    setSelectedDriverId(driverId);
    setHighlightedDriverIds(null);
    setScrollToRouteTs(null);
    setSelectedDeliveryRef(null);
    const alert = currentAlerts.find(a => a.driverId === driverId);
    setSelectedAlertId(alert ? alert.id : null);
  };
  const handleSelectDeliveryDriver = (driverId: string, deliveryRef?: string) => {
    setSelectedDriverId(driverId);
    setHighlightedDriverIds(null);
    setScrollToRouteTs(Date.now());
    setSelectedDeliveryRef(deliveryRef || null);
    const alert = currentAlerts.find(a => a.driverId === driverId);
    setSelectedAlertId(alert ? alert.id : null);
  };
  const [alertFilterRequest, setAlertFilterRequest] = useState<{
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null>(null);
  const handleFilterAlerts = (severity: 'critical' | 'warning' | 'low' | 'all') => {
    setAlertFilterRequest({
      severity,
      ts: Date.now()
    });
  };
  const handleContactDriver = (driverId: string) => {
    setCallingDriverId(driverId);
    const driver = currentDrivers.find(d => d.id === driverId);
    if (driver) {
      addLogEntry({
        type: 'contact',
        message: `Called ${driver.name}`,
        detail: `Outbound call initiated`,
        truckId: driver.truckId
      });
    }
  };
  const handleResolveAlert = (alertId: string) => {
    setResolvedAlertIds(prev => [...prev, alertId]);
    if (selectedAlertId === alertId) setSelectedAlertId(null);
    const alert = currentAlerts.find(a => a.id === alertId);
    const driver = alert ? currentDrivers.find(d => d.id === alert.driverId) : null;
    addLogEntry({
      type: 'alert-resolved',
      message: `Alert resolved: ${alert?.title || alertId}`,
      detail: alert?.description,
      truckId: driver?.truckId
    });
  };
  const handleDismissAlert = (alertId: string) => {
    setDismissedAlertIds(prev => [...prev, alertId]);
    if (selectedAlertId === alertId) setSelectedAlertId(null);
    const alert = currentAlerts.find(a => a.id === alertId);
    const driver = alert ? currentDrivers.find(d => d.id === alert.driverId) : null;
    addLogEntry({
      type: 'alert-dismissed',
      message: `Alert dismissed: ${alert?.title || alertId}`,
      detail: alert?.description,
      truckId: driver?.truckId
    });
  };
  const effectiveDrivers = useMemo(() => {
    const hasCleared = resolvedAlertIds.length > 0 || dismissedAlertIds.length > 0;
    const hasRouteOverrides = Object.keys(routeOverrides).length > 0;
    const hasStatusOverrides = Object.keys(statusOverrides).length > 0;
    if (!hasCleared && !hasRouteOverrides && !hasStatusOverrides) return currentDrivers;
    const clearedAlertIds = new Set([...resolvedAlertIds, ...dismissedAlertIds]);
    return currentDrivers.map(driver => {
      const baseDriver = routeOverrides[driver.id] ? {
        ...driver,
        route: routeOverrides[driver.id]
      } : driver;
      if (statusOverrides[driver.id]) {
        return {
          ...baseDriver,
          status: statusOverrides[driver.id] as typeof baseDriver.status
        };
      }
      if (baseDriver.status !== 'critical' && baseDriver.status !== 'warning') return baseDriver;
      const driverAlertsList = currentAlerts.filter(a => a.driverId === baseDriver.id || a.affectedDriverIds?.includes(baseDriver.id));
      const activeDriverAlerts = driverAlertsList.filter(a => !clearedAlertIds.has(a.id));
      const hasCritical = activeDriverAlerts.some(a => a.severity === 'critical');
      const hasWarning = activeDriverAlerts.some(a => a.severity === 'warning');
      let newStatus = baseDriver.status;
      if (!hasCritical && !hasWarning) newStatus = 'normal';else if (!hasCritical && hasWarning) newStatus = 'warning';else newStatus = 'critical';
      if (newStatus === baseDriver.status) return baseDriver;
      return {
        ...baseDriver,
        status: newStatus as typeof baseDriver.status
      };
    });
  }, [resolvedAlertIds, dismissedAlertIds, routeOverrides, statusOverrides, currentDrivers, currentAlerts]);
  const selectedDriver = effectiveDrivers.find(d => d.id === selectedDriverId) || null;
  const callingDriver = effectiveDrivers.find(d => d.id === callingDriverId) || null;
  const driverAlerts = selectedDriverId ? currentAlerts.filter(a => (a.driverId === selectedDriverId || a.affectedDriverIds?.includes(selectedDriverId)) && !resolvedAlertIds.includes(a.id) && !dismissedAlertIds.includes(a.id)) : [];
  return <div className="flex flex-col h-full w-full bg-slate-950 overflow-hidden" data-id="element-814">
      {/* Scripted Traffic Delay Toast */}
      <AnimatePresence data-id="element-815">
        {showToast && <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex flex-col items-center" data-id="element-816">
            <motion.div layout initial={{
          opacity: 0,
          y: 30,
          scale: 0.9
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.9
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative pointer-events-auto flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-lg shadow-2xl p-2 pr-2 h-12 overflow-hidden transition-colors cursor-pointer hover:border-slate-700" style={{
          width: 450
        }} onClick={() => {
          handleSelectAlert('a-traf-james', 'd-james');
          setShowToast(false);
        }} data-id="element-817">
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-brand-amber" data-id="element-818" />
              <div className="ml-2 text-brand-amber shrink-0" data-id="element-819">
                <AlertTriangle className="w-4 h-4" data-id="element-820" />
              </div>
              <div className="flex-1 flex items-baseline gap-2 min-w-0 truncate" data-id="element-821">
                <span className="text-xs font-semibold text-slate-200 truncate" data-id="element-822">
                  Traffic Delay
                </span>
                <span className="text-[10px] font-mono text-slate-400 shrink-0" data-id="element-823">
                  T-1105
                </span>
                <span className="text-[10px] text-slate-500 shrink-0" data-id="element-824">
                  +15m ETA
                </span>
              </div>
              <button onClick={e => {
            e.stopPropagation();
            setShowToast(false);
          }} className="relative flex items-center justify-center w-7 h-7 text-slate-600 hover:text-slate-300 rounded-full transition-colors shrink-0 ml-1 group" data-id="element-825">
                <X className="w-3.5 h-3.5 relative z-10 group-hover:text-slate-300" data-id="element-826" />
              </button>
            </motion.div>
          </div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-827">
        {callingDriver && <CallCard driver={callingDriver} onEndCall={() => setCallingDriverId(null)} data-id="element-828" />}
      </AnimatePresence>

      <TopBar onFilterAlerts={handleFilterAlerts} data-id="element-829" />

      <SuccessToasts toasts={successToasts} onDismiss={id => setSuccessToasts(prev => prev.filter(t => t.id !== id))} data-id="element-830" />

      <div className="flex-1 overflow-hidden relative" data-id="element-831">
        <div className="absolute inset-0 z-10" data-id="element-832">
          <FleetMap drivers={effectiveDrivers} selectedDriverId={selectedDriverId} highlightedDriverIds={highlightedDriverIds} hoveredDriverIds={alertHoveredDriverIds} onSelectDriver={handleSelectDriver} onDeselect={() => {
          setSelectedDriverId(null);
          setSelectedAlertId(null);
          setHighlightedDriverIds(null);
        }} demoState={demoState} isRerouting={isRerouting} hoveredRouteId={hoveredRouteId} data-id="element-833" />
        </div>

        <div className="absolute left-0 top-0 bottom-0 z-20 pointer-events-none" data-id="element-834">
          <div className="pointer-events-auto h-full" data-id="element-835">
            <LeftPanel alerts={currentAlerts} drivers={effectiveDrivers} deliveries={mockDeliveries} triageItems={mockTriageItems} selectedAlertId={selectedAlertId} selectedDriverId={selectedDriverId} onSelectAlert={handleSelectAlert} onSelectDriver={handleSelectDriver} onSelectDeliveryDriver={handleSelectDeliveryDriver} requestedAlertFilter={alertFilterRequest} onContactDriver={handleContactDriver} onHoverAlert={setAlertHoveredDriverIds} resolvedAlertIds={resolvedAlertIds} dismissedAlertIds={dismissedAlertIds} onResolveAlert={handleResolveAlert} onDismissAlert={handleDismissAlert} logEntries={logEntries} data-id="element-836" />
          </div>
        </div>

        <AnimatePresence data-id="element-837">
          {selectedDriver && <div className="absolute right-0 top-0 bottom-0 z-20" data-id="element-838">
              <DriverDetail driver={selectedDriver} alerts={driverAlerts} deliveries={mockDeliveries.filter(d => d.driverId === selectedDriverId)} scrollToRouteTs={scrollToRouteTs} selectedDeliveryRef={selectedDeliveryRef} onContactDriver={handleContactDriver} onResolveAlert={handleResolveAlert} onDismissAlert={handleDismissAlert} onUpdateRoute={handleUpdateRoute} onPreviewRoute={handlePreviewRoute} onCancelRoutePreview={handleCancelRoutePreview} onApplyReroute={handleApplyReroute} onRerouteToggle={setIsRerouting} onHoverRoute={setHoveredRouteId} data-id="element-839" />
            </div>}
        </AnimatePresence>
      </div>

      {showHidden && <>
          <DemoControls currentState={demoState} onStateChange={setDemoState} data-id="element-840" />
          <DesignNotes data-id="element-841" />
        </>}
    </div>;
}